import React, { useEffect, useRef, useState } from "react";
import "./CopyButton.scss";

import { ReactComponent as CopyIcon } from "../../assets/icons/CopyIcon.svg";
import { ReactComponent as CheckIcon } from "../../assets/icons/CheckIcon.svg";

const FEEDBACK_DURATION_MS = 2000;

// navigator.clipboard.writeText requires a secure context and, inside a
// cross-origin iframe, a `clipboard-write` Permissions-Policy grant. Both can
// be missing in real embeddings (e.g. this app loaded via the Angular
// iframe), so fall back to the legacy execCommand path, which isn't gated by
// that policy.
const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy fallback
    }
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textarea);
    return successful;
  } catch {
    return false;
  }
};

interface CopyButtonProps {
  value?: string | number | null;
  className?: string;
  title?: string;
  onCopied?: () => void;
}

const CopyButton: React.FC<CopyButtonProps> = ({
  value,
  className = "",
  title,
  onCopied,
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  if (!value) return null;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const success = await copyToClipboard(String(value));
    if (!success) {
      console.error("Failed to copy to clipboard");
      return;
    }

    setCopied(true);
    onCopied?.();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), FEEDBACK_DURATION_MS);
  };

  return (
    <button
      type="button"
      className={`copy-button ${copied ? "copied" : ""} ${className}`}
      title={title || (copied ? "Copied!" : "Copy")}
      aria-label={title || (copied ? "Copied" : "Copy to clipboard")}
      onClick={handleClick}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
};

export default CopyButton;
